import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { switchMap, map, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import { CustomTestDefinitionService } from './services/custom-test-definition.service';
import { SpreadsheetComponent } from '../../../shared/components/spreadsheet/spreadsheet.component';
import {
  InputParameter,
  OutputParameter,
  CustomTestDefinitionMode,
  SaveCustomTestDefinitionDto,
  TableColumn,
  TableDefinition,
} from './models/custom-test-definition.model';
import { Worksheet } from '../../../shared/components/spreadsheet/spreadsheet.models';

// ── Formula validation ────────────────────────────────────────────────────────

/** Characters allowed inside a formula expression. */
const FORMULA_ALLOWED_CHARS = /^[A-Za-z0-9_ +\-*/%().]+$/;
const PARAMETER_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

function buildParameterKeyBase(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return '';
  if (PARAMETER_KEY_PATTERN.test(trimmed)) {
    return trimmed;
  }

  const normalized = trimmed
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!normalized) {
    return 'param';
  }

  return /^[A-Za-z_]/.test(normalized) ? normalized : `param_${normalized}`;
}

function createUniqueParameterKey(base: string, usedKeys: Set<string>): string {
  const fallbackBase = base || 'param';
  let candidate = fallbackBase;
  let suffix = 2;

  while (usedKeys.has(candidate.toLowerCase())) {
    candidate = `${fallbackBase}_${suffix++}`;
  }

  usedKeys.add(candidate.toLowerCase());
  return candidate;
}

function formulaCharValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value ?? '';
    if (value && !FORMULA_ALLOWED_CHARS.test(value)) {
      return { formulaChars: 'Formula contains invalid characters. Allowed: A–Z a–z 0–9 _ + - * / % ( ) .' };
    }
    return null;
  };
}

function formulaParenthesesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value ?? '';
    let depth = 0;
    for (const ch of value) {
      if (ch === '(') depth++;
      if (ch === ')') depth--;
      if (depth < 0) return { formulaParens: 'Parentheses are unbalanced.' };
    }
    return depth !== 0 ? { formulaParens: 'Parentheses are unbalanced.' } : null;
  };
}

function formulaVariablesValidator(getInputKeys: () => string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value ?? '';
    if (!value) return null;

    const inputKeys = getInputKeys();
    // Extract identifiers (tokens that start with a letter or _) from formula
    const tokens = value.match(/[A-Za-z_][A-Za-z0-9_]*/g) ?? [];
    const unknown = tokens.filter(t => !inputKeys.includes(t));
    if (unknown.length > 0) {
      return { formulaVars: `Unknown variable(s): ${unknown.join(', ')}. Must match an input parameter key.` };
    }
    return null;
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'cvx-test-definition',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, SpreadsheetComponent],
  templateUrl: './test-definition.component.html',
  styleUrl: './test-definition.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestDefinitionComponent {
  @ViewChild(SpreadsheetComponent) private spreadsheetComponent?: SpreadsheetComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(CustomTestDefinitionService);
  private readonly fb = inject(FormBuilder);

  protected readonly submitAttempted = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly saveError = signal<string | null>(null);
  protected readonly spreadsheetWorksheet = signal<Worksheet>({ rows: 10, columns: 10, cells: {} });

  private readonly routeId = toSignal(
    this.route.paramMap.pipe(map(params => params.get('id'))),
    { initialValue: null }
  );

  protected readonly isEditMode = toSignal(
    this.route.paramMap.pipe(map(params => !!params.get('id'))),
    { initialValue: false }
  );

  // Build the reactive form
  protected readonly form: FormGroup = this.fb.group({
    testName: ['', [Validators.required, Validators.maxLength(255)]],
    definitionMode: ['parameters' as CustomTestDefinitionMode, [Validators.required]],
    inputParameters: this.fb.array([]),
    outputParameters: this.fb.array([], Validators.minLength(1)),
    table: this.fb.group({
      name: [''],
      key: [''],
      keyLocked: [false],
      rowCount: [1, [Validators.min(1)]],
      columnCount: [0, [Validators.min(0)]],
      columns: this.fb.array([]),
    }),
  });

  // Pre-populate when editing an existing definition
  protected readonly _ = toSignal(
    this.route.paramMap.pipe(
      map(params => params.get('id')),
      switchMap(id => id ? this.service.getById(id) : of(null))
    ).pipe(
      map((existing): null => {
        if (existing) {
          this.form.patchValue({ testName: existing.testName });
          const mode: CustomTestDefinitionMode = existing.definitionMode === 'spreadsheet' || !!existing.spreadsheet
            ? 'spreadsheet'
            : existing.definitionMode === 'table' || !!existing.table ? 'table' : 'parameters';
          this.form.get('definitionMode')?.setValue(mode, { emitEvent: false });
          if (existing.spreadsheet) {
            this.spreadsheetWorksheet.set(existing.spreadsheet);
          }
          existing.inputParameters.forEach((p: InputParameter) => this.addInputParameter(p.name, p.unit, p.key));
          existing.outputParameters.forEach((p: OutputParameter) => this.addOutputParameter(p.name, p.formula ?? '', p.unit, p.key));
          if (existing.table) {
            this.loadTable(existing.table);
          }
        }

        this.ensureParameterKeys('inputParameters');
        this.ensureParameterKeys('outputParameters');
        this.ensureTableKeys();
        this.refreshFormulaValidators();
        this.refreshTableFormulaValidators();
        this.updateModeState();
        return null;
      })
    )
  );

  // ── FormArray accessors ───────────────────────────────────────────────────

  get inputParameters(): FormArray {
    return this.form.get('inputParameters') as FormArray;
  }

  get outputParameters(): FormArray {
    return this.form.get('outputParameters') as FormArray;
  }

  get tableGroup(): FormGroup {
    return this.form.get('table') as FormGroup;
  }

  get tableColumns(): FormArray {
    return this.tableGroup.get('columns') as FormArray;
  }

  protected readonly definitionModeOptions: Array<{ value: CustomTestDefinitionMode; label: string; description: string }> = [
    { value: 'parameters', label: 'Input / Output Parameters', description: 'Use flexible parameter lists with optional formulas.' },
    { value: 'table', label: 'Table', description: 'Use fixed headers and rows for grid-based data entry.' },
    { value: 'spreadsheet', label: 'Spreadsheet', description: 'Use an Excel-style worksheet with formulas and automatic recalculation.' },
  ];

  /** Current list of input parameter names for formula variable validation. */
  private getInputParamNames(): string[] {
    return this.inputParameters.controls.map(c => (c.get('name')?.value ?? '').trim()).filter(Boolean);
  }

  private getInputParamKeys(): string[] {
    return this.inputParameters.controls.map(c => (c.get('key')?.value ?? '').trim()).filter(Boolean);
  }

  private getOutputParamKeys(): string[] {
    return this.outputParameters.controls.map(c => (c.get('key')?.value ?? '').trim()).filter(Boolean);
  }

  private getTableInputKeys(): string[] {
    return this.tableColumns.controls
      .filter(control => (control.get('kind')?.value ?? 'input') === 'input')
      .map(control => (control.get('key')?.value ?? '').trim())
      .filter(Boolean);
  }

  private getTableFormulaKeys(): string[] {
    const inputKeys = this.getTableInputKeys();
    const allColumnKeys = this.tableColumns.controls
      .map(control => (control.get('key')?.value ?? '').trim())
      .filter(Boolean);
    const sumKeys = allColumnKeys.map(key => `${key}_sum`);
    return Array.from(new Set([...inputKeys, ...sumKeys]));
  }

  private tableCellKey(rowIndex: number, columnKey: string): string {
    const tableKey = (this.tableGroup.get('key')?.value ?? '').trim() || 'table';
    return `${tableKey}__r${rowIndex + 1}__${columnKey}`;
  }

  protected isParametersMode(): boolean {
    return (this.form.get('definitionMode')?.value ?? 'parameters') === 'parameters';
  }

  protected isTableMode(): boolean {
    return (this.form.get('definitionMode')?.value ?? 'parameters') === 'table';
  }

  protected isSpreadsheetMode(): boolean {
    return (this.form.get('definitionMode')?.value ?? 'parameters') === 'spreadsheet';
  }

  protected onDefinitionModeChanged(): void {
    this.updateModeState();
  }

  private updateModeState(): void {
    if (this.isTableMode()) {
      this.inputParameters.disable({ emitEvent: false });
      this.outputParameters.disable({ emitEvent: false });
      this.tableGroup.enable({ emitEvent: false });
    } else if (this.isParametersMode()) {
      this.tableGroup.disable({ emitEvent: false });
      this.inputParameters.enable({ emitEvent: false });
      this.outputParameters.enable({ emitEvent: false });
    } else {
      this.tableGroup.disable({ emitEvent: false });
      this.inputParameters.disable({ emitEvent: false });
      this.outputParameters.disable({ emitEvent: false });
    }
  }

  private ensureParameterKeys(arrayName: 'inputParameters' | 'outputParameters'): void {
    const formArray = this.form.get(arrayName) as FormArray;
    const usedKeys = new Set<string>(
      (arrayName === 'inputParameters' ? this.getOutputParamKeys() : this.getInputParamKeys())
        .map(key => key.toLowerCase())
    );

    formArray.controls.forEach(control => {
      const name = (control.get('name')?.value ?? '').trim();
      const keyControl = control.get('key');
      const keyLocked = !!control.get('keyLocked')?.value;
      const existingKey = (keyControl?.value ?? '').trim();

      if (keyLocked && existingKey && PARAMETER_KEY_PATTERN.test(existingKey) && !usedKeys.has(existingKey.toLowerCase())) {
        usedKeys.add(existingKey.toLowerCase());
        return;
      }

      const keyBase = buildParameterKeyBase(name);
      if (!keyBase) {
        if (existingKey) {
          keyControl?.setValue('', { emitEvent: false });
        }
        return;
      }

      const generatedKey = createUniqueParameterKey(keyBase, usedKeys);
      if (existingKey !== generatedKey) {
        keyControl?.setValue(generatedKey, { emitEvent: false });
      }
    });
  }

  private ensureTableKeys(): void {
    const keyControl = this.tableGroup.get('key');
    const keyLockedControl = this.tableGroup.get('keyLocked');
    const name = (this.tableGroup.get('name')?.value ?? '').trim();
    const existingKey = (keyControl?.value ?? '').trim();
    const keyLocked = !!keyLockedControl?.value;

    if (keyLocked && existingKey && PARAMETER_KEY_PATTERN.test(existingKey)) {
      // keep existing key
    } else {
      const keyBase = buildParameterKeyBase(name);
      if (keyBase) {
        const generatedKey = createUniqueParameterKey(keyBase, new Set<string>());
        if (existingKey !== generatedKey) {
          keyControl?.setValue(generatedKey, { emitEvent: false });
        }
      }
    }

    const usedKeys = new Set<string>();
    this.tableColumns.controls.forEach(control => {
      const nameControl = control.get('name');
      const columnKeyControl = control.get('key');
      const columnKeyLocked = !!control.get('keyLocked')?.value;
      const existingColumnKey = (columnKeyControl?.value ?? '').trim();
      const columnName = (nameControl?.value ?? '').trim();

      if (columnKeyLocked && existingColumnKey && PARAMETER_KEY_PATTERN.test(existingColumnKey) && !usedKeys.has(existingColumnKey.toLowerCase())) {
        usedKeys.add(existingColumnKey.toLowerCase());
        return;
      }

      const keyBase = buildParameterKeyBase(columnName);
      if (!keyBase) {
        if (existingColumnKey) {
          columnKeyControl?.setValue('', { emitEvent: false });
        }
        return;
      }

      const generatedKey = createUniqueParameterKey(keyBase, usedKeys);
      if (existingColumnKey !== generatedKey) {
        columnKeyControl?.setValue(generatedKey, { emitEvent: false });
      }
    });

    this.tableGroup.get('columnCount')?.setValue(this.tableColumns.length, { emitEvent: false });
  }

  private refreshTableFormulaValidators(): void {
    this.tableColumns.controls.forEach(ctrl => {
      const formulaCtrl = ctrl.get('formula');
      if (formulaCtrl) {
        formulaCtrl.setValidators([
          formulaCharValidator(),
          formulaParenthesesValidator(),
          formulaVariablesValidator(() => this.getTableFormulaKeys()),
        ]);
        formulaCtrl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  private loadTable(table: TableDefinition): void {
    this.tableGroup.patchValue({
      name: table.name,
      key: table.key,
      keyLocked: !!table.key,
      rowCount: table.rowCount || 1,
      columnCount: table.columnCount || table.columns?.length || 0,
    });

    this.tableColumns.clear();
    (table.columns ?? []).forEach(column => this.addTableColumn(column.name, column.kind, column.unit, column.formula ?? '', column.key));
    this.syncTableColumnCount();
  }

  private syncTableColumnCount(): void {
    const desired = Number(this.tableGroup.get('columnCount')?.value ?? 0);
    const safeDesired = Number.isFinite(desired) && desired >= 0 ? Math.floor(desired) : 0;

    while (this.tableColumns.length < safeDesired) {
      this.addTableColumn();
    }

    while (this.tableColumns.length > safeDesired) {
      this.tableColumns.removeAt(this.tableColumns.length - 1);
    }

    this.ensureTableKeys();
    this.refreshTableFormulaValidators();
  }

  protected onTableNameChanged(): void {
    this.ensureTableKeys();
  }

  protected onTableColumnCountChanged(): void {
    this.syncTableColumnCount();
  }

  protected onTableColumnNameChanged(): void {
    this.ensureTableKeys();
    this.refreshTableFormulaValidators();
  }

  protected onTableColumnKindChanged(): void {
    this.refreshTableFormulaValidators();
  }

  // ── Input parameters ──────────────────────────────────────────────────────

  addInputParameter(name = '', unit = '', key = ''): void {
    const trimmedKey = key.trim();
    const group = this.fb.group({
      name: [name, [Validators.required]],
      key: [trimmedKey],
      keyLocked: [trimmedKey.length > 0],
      unit: [unit],
    });
    this.inputParameters.push(group);
    this.ensureParameterKeys('inputParameters');
    // Re-validate all output formula fields when input params change
    this.refreshFormulaValidators();
  }

  removeInputParameter(index: number): void {
    this.inputParameters.removeAt(index);
    this.ensureParameterKeys('inputParameters');
    this.refreshFormulaValidators();
  }

  protected onInputParameterNameChanged(): void {
    this.ensureParameterKeys('inputParameters');
    this.refreshFormulaValidators();
  }

  // ── Output parameters ─────────────────────────────────────────────────────

  addOutputParameter(name = '', formula = '', unit = '', key = ''): void {
    const trimmedKey = key.trim();
    const group = this.fb.group({
      name: [name, [Validators.required]],
      key: [trimmedKey],
      keyLocked: [trimmedKey.length > 0],
      unit: [unit],
      formula: [
        formula,
        [
          formulaCharValidator(),
          formulaParenthesesValidator(),
          formulaVariablesValidator(() => this.getInputParamKeys()),
        ],
      ],
    });
    this.outputParameters.push(group);
    this.ensureParameterKeys('outputParameters');
  }

  removeOutputParameter(index: number): void {
    this.outputParameters.removeAt(index);
    this.ensureParameterKeys('outputParameters');
  }

  protected onOutputParameterNameChanged(): void {
    this.ensureParameterKeys('outputParameters');
  }

  // ── Table definition ─────────────────────────────────────────────────────

  addTableColumn(
    name = '',
    kind: 'input' | 'output' = 'input',
    unit = '',
    formula = '',
    key = ''
  ): void {
    const trimmedKey = key.trim();
    const group = this.fb.group({
      name: [name, [Validators.required]],
      key: [trimmedKey],
      keyLocked: [trimmedKey.length > 0],
      kind: [kind, [Validators.required]],
      unit: [unit],
      formula: [formula],
    });
    this.tableColumns.push(group);
    this.ensureTableKeys();
    this.refreshTableFormulaValidators();
  }

  removeTableColumn(index: number): void {
    this.tableColumns.removeAt(index);
    this.ensureTableKeys();
    this.refreshTableFormulaValidators();
  }

  protected tableInputKeys(): string[] {
    return this.getTableFormulaKeys();
  }

  protected tableColumnKey(group: AbstractControl): string {
    return (group.get('key')?.value ?? '').trim();
  }

  protected tableColumnFormula(group: AbstractControl): string {
    return (group.get('formula')?.value ?? '').trim();
  }

  protected isTableColumnOutput(group: AbstractControl): boolean {
    return (group.get('kind')?.value ?? 'input') === 'output';
  }

  // ── Formula helper ────────────────────────────────────────────────────────

  /** Re-run formula validators after input parameter names change. */
  private refreshFormulaValidators(): void {
    this.outputParameters.controls.forEach(ctrl => {
      const formulaCtrl = ctrl.get('formula');
      if (formulaCtrl) {
        formulaCtrl.setValidators([
          formulaCharValidator(),
          formulaParenthesesValidator(),
          formulaVariablesValidator(() => this.getInputParamKeys()),
        ]);
        formulaCtrl.updateValueAndValidity();
      }
    });
  }

  // ── Uniqueness helpers ────────────────────────────────────────────────────

  protected inputNamesUnique(): boolean {
    const names = this.getInputParamNames();
    return new Set(names.map(n => n.toLowerCase())).size === names.length;
  }

  protected outputNamesUnique(): boolean {
    const names = this.outputParameters.controls
      .map(c => (c.get('name')?.value ?? '').trim().toLowerCase())
      .filter(Boolean);
    return new Set(names).size === names.length;
  }

  // ── Available parameters helper (for template display) ───────────────────

  protected get availableInputKeys(): string[] {
    return this.getInputParamKeys();
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  protected save(): void {
    this.submitAttempted.set(true);
    this.ensureParameterKeys('inputParameters');
    this.ensureParameterKeys('outputParameters');
    this.refreshFormulaValidators();
    this.ensureTableKeys();
    this.refreshTableFormulaValidators();
    this.updateModeState();

    if (this.form.invalid) {
      return;
    }

    if (this.isParametersMode() && (!this.inputNamesUnique() || !this.outputNamesUnique())) {
      return;
    }

    if (this.isTableMode() && this.tableColumns.length === 0) {
      return;
    }

    const rawTable = this.tableGroup.value as {
      name: string;
      key: string;
      rowCount: number;
      columnCount: number;
      columns: Array<{ name: string; key: string; kind: 'input' | 'output'; unit: string; formula?: string | null }>;
    };

    const table: TableDefinition | null = rawTable.name?.trim() && this.tableColumns.length > 0
      ? {
          name: rawTable.name.trim(),
          key: (rawTable.key ?? '').trim(),
          rowCount: Math.max(1, Math.floor(Number(rawTable.rowCount ?? 1) || 1)),
          columnCount: this.tableColumns.length,
          columns: rawTable.columns.map(column => ({
            name: column.name.trim(),
            key: column.key.trim(),
            kind: column.kind,
            unit: column.unit.trim(),
            formula: (column.formula ?? '').trim(),
          })),
        }
      : null;

    const dto: SaveCustomTestDefinitionDto = {
      testName: this.form.value.testName.trim(),
      definitionMode: this.isSpreadsheetMode() ? 'spreadsheet' : this.isTableMode() ? 'table' : 'parameters',
      inputParameters: this.isParametersMode()
        ? this.inputParameters.getRawValue().map((p: { name: string; key: string; unit: string }) => ({
            name: p.name.trim(),
            key: p.key.trim(),
            unit: p.unit.trim(),
          }))
        : [],
      outputParameters: this.isParametersMode()
        ? this.outputParameters.getRawValue().map((p: { name: string; key: string; unit: string; formula: string }) => ({
            name: p.name.trim(),
            key: p.key.trim(),
            unit: p.unit.trim(),
            formula: (p.formula ?? '').trim(),
          }))
        : [],
      table: this.isTableMode() ? table : null,
      spreadsheet: this.isSpreadsheetMode()
        ? this.spreadsheetComponent?.getWorksheet() ?? this.spreadsheetWorksheet()
        : null,
    };

    this.isSaving.set(true);
    this.saveError.set(null);

    const id = this.routeId();
    const request$ = id ? this.service.update(id, dto) : this.service.create(dto);

    request$.subscribe({
      next: () => this.router.navigate(['/tools/custom-test-definitions']),
      error: () => {
        this.saveError.set('An error occurred while saving. Please try again.');
        this.isSaving.set(false);
      },
    });
  }

  protected cancel(): void {
    this.router.navigate(['/tools/custom-test-definitions']);
  }
}
